import { Header } from './Header';
import { Footer } from './Footer';
import { SinglePost } from './SinglePost';
import { BlockRenderer } from '@/components/BlockRenderer';
import { FiltersProvider, PlansContext, AppDataContext, FilteredPlansProvider } from '@/contexts';
import type { FilterValue } from '@/contexts/FiltersContext.context';
import { ScrollToTop, EditLink } from '@/components/atoms';
import { FooterCampaign } from '@/components/molecules';
import { Habillage } from '@/components/organisms';
import { Archive, BlankPage, NotFound, Page, SubComparator } from '@/components/pages';
import { Home } from './Home';
import type { LayoutProps } from '@/types/app';
import '@/styles/_post-card.scss';
import '@/styles/_top3-plans.scss';
import '@/styles/_specific-plan.scss';

function extractInitialFilters(data: LayoutProps['data']): Record<string, FilterValue> {
  // Merges the two possible sources to align SSR and CSR:
  //   1. Sub-comparator ACF (`data.subComparator.initialFilters`)
  //   2. `initialFilters` attribute on a Gutenberg block in `data.blocks`
  // The block wins over ACF key by key (a FilterGroup placed in the page
  // can explicitly override it). Without this merge, the old FilterGroup
  // `useEffect` only applied on CSR and created a divergence with the SSR
  // render (state = ACF only) → different plan list after hydration.
  const merged: Record<string, FilterValue> = {};

  if (data.subComparator?.initialFilters) {
    Object.assign(merged, data.subComparator.initialFilters);
  }

  for (const block of (data.blocks ?? [])) {
    // A block's attributes are freeform: depending on the block, initial
    // filters sit at the root or under `props`.
    const attributes = block.attributes as Record<string, unknown> | undefined;
    const nested = attributes?.props as Record<string, unknown> | undefined;
    const filters = nested?.initialFilters ?? attributes?.initialFilters;
    if (filters && typeof filters === 'object' && Object.keys(filters).length > 0) {
      Object.assign(merged, filters);
      break;
    }
  }

  return merged;
}

export function Layout({ data }: LayoutProps) {
  const is404 = data.context.is_404;

  return (
    <AppDataContext value={data}>
    <PlansContext value={{ mobilePlans: data.mobilePlans ?? [], internetPlans: data.internetPlans ?? [] }}>
    <FiltersProvider initialFilters={extractInitialFilters(data)}>
    <FilteredPlansProvider>
      <div className="app">
        <div className="app-container">
          <Header
            siteName={data.site.name}
            primary={data.menus?.primary ?? []}
            secondary={data.menus?.secondary ?? []}
          />
        </div>
      </div>
      <Habillage habillage={data.habillage}>
        <div className="app">
          <div className="app-container">
            <main className="main">
              {is404 ? (
                <NotFound />
              ) : data.context.is_single ? (
                <SinglePost data={data} />
              ) : data.context.is_front_page ? (
                <Home data={data} />
              ) : (data.context.is_home || data.context.is_archive) ? (
                <Archive data={data} />
              ) : data.post?.template === 'template-sub-comparator.php' ? (
                <SubComparator data={data} />
              ) : data.post?.template === 'template-page-vide.php' ? (
                <BlankPage data={data} />
              ) : data.context.is_page ? (
                <Page data={data} />
              ) : (
                <BlockRenderer blocks={data.blocks} />
              )}
              <Footer siteName={data.site.name} items={data.menus?.footer ?? []} />
            </main>
          </div>
          <ScrollToTop />
          <EditLink />
        </div>
      </Habillage>
      <FooterCampaign campaign={data.footerCampaign} />
    </FilteredPlansProvider>
    </FiltersProvider>
    </PlansContext>
    </AppDataContext>
  );
}
